import { useState, useEffect } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, TrendingDown, Users, Building2, Calendar, CreditCard, DollarSign, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSuperAdmin } from "@/hooks/use-super-admin";
import { useNavigate } from "react-router-dom";
import { format, subDays, subWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { StatCard } from "@/components/ui/stat-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

interface PlatformMetrics {
  totalBusinesses: number;
  activeBusinesses: number;
  suspendedBusinesses: number;
  totalRevenue: number;
  revenueChange: number;
  totalAppointments: number;
  appointmentsChange: number;
  totalCustomers: number;
  customersChange: number;
  newSignups: number;
  signupsChange: number;
  averageRevenuePerBusiness: number;
  mostPopularModule: string;
}

interface BusinessGrowth {
  date: string;
  count: number;
}

interface RevenueTrend {
  date: string;
  amount: number;
}

export default function SuperAdminAnalytics() {
  const { isSuperAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "all">("30d");
  const [businessGrowth, setBusinessGrowth] = useState<BusinessGrowth[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrend[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/app");
      return;
    }
    fetchAnalytics();
  }, [isSuperAdmin, navigate, timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      let startDate: Date;
      if (timeRange === "7d") {
        startDate = subDays(now, 7);
      } else if (timeRange === "30d") {
        startDate = subDays(now, 30);
      } else if (timeRange === "90d") {
        startDate = subDays(now, 90);
      } else {
        startDate = new Date(0); // All time
      }

      // Fetch all businesses
      const { data: businesses, error: businessesError } = await supabase
        .from("businesses")
        .select("id, created_at, subscription_status, primary_module");

      if (businessesError) throw businessesError;

      const totalBusinesses = businesses?.length || 0;
      const activeBusinesses = businesses?.filter(b => b.subscription_status === "active").length || 0;
      const suspendedBusinesses = businesses?.filter(b => b.subscription_status === "suspended").length || 0;

      // Calculate new signups
      const newSignups = businesses?.filter(b => new Date(b.created_at) >= startDate).length || 0;
      const previousStartDate = timeRange === "all" ? new Date(0) : subDays(startDate, timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90);
      const previousSignups = businesses?.filter(b => {
        const created = new Date(b.created_at);
        return created >= previousStartDate && created < startDate;
      }).length || 0;
      const signupsChange = previousSignups > 0 ? ((newSignups - previousSignups) / previousSignups) * 100 : 0;

      // Fetch appointments
      const { count: totalAppointments } = await supabase
        .from("appointments")
        .select("*", { count: "exact", head: true });

      const { count: recentAppointments } = await supabase
        .from("appointments")
        .select("*", { count: "exact", head: true })
        .gte("created_at", startDate.toISOString());

      const previousAppointments = (totalAppointments || 0) - (recentAppointments || 0);
      const appointmentsChange = previousAppointments > 0 ? ((recentAppointments || 0) - previousAppointments) / previousAppointments * 100 : 0;

      // Fetch customers
      const { count: totalCustomers } = await supabase
        .from("customers")
        .select("*", { count: "exact", head: true });

      const { count: recentCustomers } = await supabase
        .from("customers")
        .select("*", { count: "exact", head: true })
        .gte("created_at", startDate.toISOString());

      const previousCustomers = (totalCustomers || 0) - (recentCustomers || 0);
      const customersChange = previousCustomers > 0 ? ((recentCustomers || 0) - previousCustomers) / previousCustomers * 100 : 0;

      // Calculate revenue
      let totalRevenue = 0;
      let previousRevenue = 0;

      // Completed appointments revenue
      const { data: completedAppointments } = await supabase
        .from("appointments")
        .select("total_price_amount, service:services(price_amount), created_at")
        .eq("status", "completed");

      const appointmentRevenue = (completedAppointments || []).reduce(
        (sum, a: any) => {
          const amount = Number(a.total_price_amount) || Number(a.service?.price_amount) || 0;
          if (new Date(a.created_at) >= startDate) {
            totalRevenue += amount;
          } else {
            previousRevenue += amount;
          }
          return sum + amount;
        },
        0
      );

      // Invoice revenue
      const { data: paidInvoices } = await supabase
        .from("invoices")
        .select("id, invoice_items(*), invoice_adjustments(*), created_at")
        .eq("status", "paid");

      if (paidInvoices) {
        for (const inv of paidInvoices) {
          const items = (inv as any).invoice_items || [];
          const adjustments = (inv as any).invoice_adjustments || [];
          const subtotal = items.reduce(
            (s: number, it: any) => s + Number(it.quantity || 0) * Number(it.unit_price || 0),
            0
          );
          const taxes = adjustments
            .filter((a: any) => a.type === "tax" && !a.included_in_prices)
            .reduce((s: number, a: any) => {
              if (a.mode === "percent") {
                return s + (subtotal * Number(a.percent_rate || 0)) / 100;
              }
              return s + Number(a.amount || 0);
            }, 0);
          const discounts = adjustments
            .filter((a: any) => a.type === "discount")
            .reduce((s: number, a: any) => {
              if (a.mode === "percent") {
                return s + (subtotal * Number(a.percent_rate || 0)) / 100;
              }
              return s + Number(a.amount || 0);
            }, 0);
          const invoiceTotal = subtotal + taxes - discounts;
          
          if (new Date((inv as any).created_at) >= startDate) {
            totalRevenue += invoiceTotal;
          } else {
            previousRevenue += invoiceTotal;
          }
        }
      }

      // Day passes revenue
      const { data: dayPasses } = await supabase
        .from("day_passes")
        .select("amount, created_at")
        .eq("payment_status", "paid");

      (dayPasses || []).forEach((dp: any) => {
        if (new Date(dp.created_at) >= startDate) {
          totalRevenue += Number(dp.amount || 0);
        } else {
          previousRevenue += Number(dp.amount || 0);
        }
      });

      // Membership revenue
      const { data: membershipPayments } = await supabase
        .from("platform_payments")
        .select("gross_amount, paid_at")
        .eq("context", "membership_payment")
        .eq("status", "success");

      (membershipPayments || []).forEach((p: any) => {
        const paidAt = p.paid_at ? new Date(p.paid_at) : new Date();
        if (paidAt >= startDate) {
          totalRevenue += Number(p.gross_amount || 0);
        } else {
          previousRevenue += Number(p.gross_amount || 0);
        }
      });

      const revenueChange = previousRevenue > 0 ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      // Calculate average revenue per business
      const averageRevenuePerBusiness = activeBusinesses > 0 ? totalRevenue / activeBusinesses : 0;

      // Find most popular module
      const moduleCounts = new Map<string, number>();
      businesses?.forEach(b => {
        const module = b.primary_module || "appointments";
        moduleCounts.set(module, (moduleCounts.get(module) || 0) + 1);
      });
      const mostPopularModule = Array.from(moduleCounts.entries())
        .sort((a, b) => b[1] - a[1])[0]?.[0] || "appointments";

      setMetrics({
        totalBusinesses,
        activeBusinesses,
        suspendedBusinesses,
        totalRevenue,
        revenueChange,
        totalAppointments: totalAppointments || 0,
        appointmentsChange,
        totalCustomers: totalCustomers || 0,
        customersChange,
        newSignups,
        signupsChange,
        averageRevenuePerBusiness,
        mostPopularModule,
      });

      // Fetch business growth data (cumulative by week)
      const growthData: BusinessGrowth[] = [];
      
      // Calculate number of weeks based on time range
      let weeks: number;
      if (timeRange === "7d") {
        weeks = 1; // 1 week
      } else if (timeRange === "30d") {
        weeks = 4; // 4 weeks
      } else if (timeRange === "90d") {
        weeks = 13; // ~13 weeks
      } else {
        weeks = 52; // 52 weeks for all time
      }
      
      let cumulativeCount = 0;
      
      // Get all businesses created before the start date
      const businessesBeforeStart = businesses?.filter(b => new Date(b.created_at) < startDate).length || 0;
      cumulativeCount = businessesBeforeStart;
      
      // Group businesses by week
      for (let i = weeks - 1; i >= 0; i--) {
        const weekEnd = subWeeks(now, i);
        const weekStart = startOfWeek(weekEnd, { weekStartsOn: 1 }); // Monday as start of week
        const weekEndDate = endOfWeek(weekEnd, { weekStartsOn: 1 });
        
        // Count businesses created in this week
        const newBusinesses = businesses?.filter(b => {
          const created = new Date(b.created_at);
          return created >= weekStart && created <= weekEndDate;
        }).length || 0;
        
        cumulativeCount += newBusinesses;
        
        // Format week label (e.g., "Jan 1 - Jan 7" or just "Week of Jan 1")
        const dateStr = format(weekStart, "MMM d");
        
        growthData.push({ 
          date: dateStr, 
          count: cumulativeCount 
        });
      }
      setBusinessGrowth(growthData);

    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <AppShell businessName="Super Admin">
        <PageHeader title="Platform Analytics" description="Platform-wide metrics and insights" />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell businessName="Super Admin">
      <PageHeader
        title="Platform Analytics"
        description="Platform-wide metrics and insights"
        action={
          <Select value={timeRange} onValueChange={(v: "7d" | "30d" | "90d" | "all") => setTimeRange(v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {metrics && (
        <>
          {/* Key Metrics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatCard
              title="Total Businesses"
              value={metrics.totalBusinesses.toLocaleString()}
              icon={Building2}
              description={`${metrics.activeBusinesses} active, ${metrics.suspendedBusinesses} suspended`}
            />
            <StatCard
              title="Total Revenue"
              value={`₵${metrics.totalRevenue.toLocaleString()}`}
              icon={DollarSign}
              change={metrics.revenueChange}
              description={`₵${metrics.averageRevenuePerBusiness.toFixed(0)} avg per business`}
            />
            <StatCard
              title="Total Appointments"
              value={metrics.totalAppointments.toLocaleString()}
              icon={Calendar}
              change={metrics.appointmentsChange}
            />
            <StatCard
              title="Total Customers"
              value={metrics.totalCustomers.toLocaleString()}
              icon={Users}
              change={metrics.customersChange}
            />
          </div>

          {/* Secondary Metrics */}
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">New Signups</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.newSignups}</div>
                <div className="flex items-center text-xs text-muted-foreground mt-1">
                  {metrics.signupsChange >= 0 ? (
                    <TrendingUp className="h-3 w-3 mr-1 text-green-600" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1 text-red-600" />
                  )}
                  <span>{Math.abs(metrics.signupsChange).toFixed(1)}% change</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Most Popular Module</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">{metrics.mostPopularModule}</div>
                <p className="text-xs text-muted-foreground mt-1">Primary business module</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Businesses</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.activeBusinesses}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {((metrics.activeBusinesses / metrics.totalBusinesses) * 100).toFixed(1)}% of total
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Business Growth Chart */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Business Growth</CardTitle>
            </CardHeader>
            <CardContent>
              {businessGrowth.length > 0 ? (
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={businessGrowth}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: 'currentColor' }}
                        angle={timeRange === "all" ? -45 : 0}
                        textAnchor={timeRange === "all" ? "end" : "middle"}
                        height={timeRange === "all" ? 60 : 30}
                        interval={timeRange === "all" ? Math.floor(businessGrowth.length / 10) : "preserveStartEnd"}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: 'currentColor' }}
                        width={60}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-sm font-medium text-muted-foreground">
                                      {payload[0].payload.date}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                                    <span className="text-sm font-medium">
                                      {payload[0].value} businesses
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        fill="url(#colorCount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <p>No data available for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </AppShell>
  );
}
